// src/pages/Admin/ManageGallery.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../../../context/AdminContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const ManageGallery = () => {
  const { token } = useAdmin();
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [message, setMessage] = useState("");

  const authToken = token || localStorage.getItem("adminToken");

  // ✅ Fetch images (FIXED)
  const fetchImages = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/gallery`);
      setImages(data);
    } catch (err) {
      console.error(err);
      setMessage("Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  // Group by category
  const groupedImages = useMemo(() => {
    return images.reduce((acc, img) => {
      const category = img.category || "Others";
      if (!acc[category]) acc[category] = [];
      acc[category].push(img);
      return acc;
    }, {});
  }, [images]);

  // Toggle select
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  // Select all toggle
  const toggleSelectAll = (categoryImages) => {
    const ids = categoryImages.map((img) => img._id);
    const allSelected = ids.every((id) => selected.includes(id));

    if (allSelected) {
      setSelected((prev) =>
        prev.filter((id) => !ids.includes(id))
      );
    } else {
      setSelected((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  // ✅ Single delete (FIXED)
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;

    try {
      setDeletingId(id);

      await axios.delete(`${API}/api/gallery/${id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      setImages((prev) => prev.filter((img) => img._id !== id));
      setSelected((prev) => prev.filter((sid) => sid !== id));

      setMessage("Image deleted successfully");
    } catch (err) {
      console.error(err);
      setMessage("Failed to delete image");
    } finally {
      setDeletingId(null);
    }
  };

  // ✅ Bulk delete (FIXED)
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} images?`)) return;

    try {
      setBulkDeleting(true);

      await axios.delete(`${API}/api/gallery/bulk`, {
        data: { ids: selected },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      setImages((prev) =>
        prev.filter((img) => !selected.includes(img._id))
      );

      setSelected([]);
      setMessage("Selected images deleted successfully");
    } catch (err) {
      console.error(err);
      setMessage("Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const getTitle = (img) => {
    if (img.title?.trim()) return img.title;
    if (img.linkedProductId?.name) return img.linkedProductId.name;
    if (img.linkedProjectId?.name) return img.linkedProjectId.name;
    return "Untitled";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 p-6">

      {/* TOP BAR */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="text-white bg-white/10 px-4 py-2 rounded-lg"
        >
          ← Back to Dashboard
        </button>

        <h1 className="text-white text-2xl font-bold">
          Manage Gallery
        </h1>

        <button
          onClick={handleBulkDelete}
          disabled={selected.length === 0 || bulkDeleting}
          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          {bulkDeleting ? (
            "Deleting..."
          ) : (
            <>
              <FiTrash2 size={16} />
              Delete Selected ({selected.length})
            </>
          )}
        </button>
      </div>

      {/* MESSAGE */}
      {message && (
        <div className="mb-4 px-4 py-2 rounded-lg text-sm bg-white/10 text-white border border-white/10">
          {message}
        </div>
      )}

      {/* CONTENT */}
      {loading ? (
        <p className="text-gray-400">Loading...</p>
      ) : Object.keys(groupedImages).length === 0 ? (
        <p className="text-gray-400">No images found</p>
      ) : (
        Object.entries(groupedImages).map(([category, imgs]) => (
          <div key={category} className="mb-10">

            <div className="flex justify-between items-center mb-3">
              <h2 className="text-white text-lg font-semibold">
                {category}
              </h2>

              <button
                onClick={() => toggleSelectAll(imgs)}
                className="text-sm text-blue-400 hover:underline"
              >
                Select All
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {imgs.map((img) => {
                const isSelected = selected.includes(img._id);

                return (
                  <div
                    key={img._id}
                    className={`relative bg-white/10 rounded-xl overflow-hidden border ${
                      isSelected
                        ? "border-blue-500"
                        : "border-white/10"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(img._id)}
                      className="absolute top-2 left-2 w-4 h-4"
                    />

                    <img
                      src={img.url}
                      alt={getTitle(img)}
                      className="w-full h-40 object-cover"
                    />

                    <div className="p-3">
                      <p className="text-white text-sm truncate">
                        {getTitle(img)}
                      </p>

                      <button
                        onClick={() => handleDelete(img._id)}
                        className="mt-2 w-full flex items-center justify-center gap-1 bg-red-500 hover:bg-red-600 text-white py-1 rounded"
                      >
                        {deletingId === img._id ? (
                          "Deleting..."
                        ) : (
                          <>
                            <FiTrash2 size={14} />
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ManageGallery;
